import { Component, DoCheck, inject, Input, OnChanges } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PromptCardComponent } from '../../../toasts-and-cards/prompt-card/prompt-card.component';
import { EditMessageComponent } from '../edit-message/edit-message.component';
import { MessageSchedulesComponent } from '../message-schedules/message-schedules.component';
import {
  CampaignDetails,
  EngagementMessage,
  EngagementMessagesSchedules,
} from '../../../../pages/organization/campaigns/types/campaignDetails';
import { CampaignsService } from '../../../../services/campaign/campaigns-api.service';
import { MessageMeta } from '../../../../services/campaign/campaign-insights.service';
import { SmsBubbleComponent } from '../../../messages/sms-bubble/sms-bubble.component';
import { DeliveryComponent } from '../../../messages/delivery/delivery.component';
import { StatusComponent } from '../../../ui/status/status.component';
import { formatCountdown, formatWhen } from '../../../../../utils/sms';
import {
  isStopped,
  messageDelivery,
  scheduleMoment,
  schedulesOf,
  subjectOf,
  withMeta,
} from '../../../../pages/organization/campaigns/campaign-details/campaign-sends';

interface ScheduleRow {
  key: string | number;
  when: string;
  exact: string;
  zone: string | null;
  past: boolean;
  countdown: string;
  status: string;
  stopped: boolean;
}

/**
 * One campaign message: the copy as a phone shows it (with its SMS parts),
 * how it was delivered, and when it goes out.
 */
@Component({
  selector: 'app-message-content-card',
  standalone: true,
  imports: [
    MatTooltipModule,
    PromptCardComponent,
    EditMessageComponent,
    MessageSchedulesComponent,
    SmsBubbleComponent,
    DeliveryComponent,
    StatusComponent,
  ],
  templateUrl: './message-content-card.component.html',
  styleUrl: './message-content-card.component.css',
})
export class MessageContentCardComponent implements OnChanges, DoCheck {
  showSchedule = false;
  showEdit = false;
  showDelete = false;
  messageSchedules!: EngagementMessagesSchedules[];
  campaignsService = inject(CampaignsService);
  @Input() message!: EngagementMessage;
  @Input() campaignData!: CampaignDetails;
  /** The message's own details (uuid, sender, status), which the campaign leaves out. */
  @Input() meta: MessageMeta | null | undefined = null;
  /** Position in the campaign, for the "Message 2" label. */
  @Input() index: number | null = null;

  subject = '';
  copy = '';
  from = '';
  uuid: string | null = null;
  schedules: ScheduleRow[] = [];
  deliveryStatus = '';
  sentAt: Date | null = null;

  /** The schedules list the rows were built from. */
  private builtFrom: unknown = null;

  ngOnChanges() {
    this.build();
  }

  /**
   * CampaignsService fills in each message's schedules on the same message
   * object after the first emission, so the input does not change; rebuild
   * when the schedules list itself is replaced.
   */
  ngDoCheck() {
    if (this.message && (this.message.engagementMessageSchedules ?? null) !== this.builtFrom) this.build();
  }

  private build() {
    const now = new Date();
    const message = this.message ? withMeta(this.message, this.meta) : this.message;
    this.builtFrom = this.message?.engagementMessageSchedules ?? null;
    this.subject = subjectOf(message);
    this.copy = String(message?.content || message?.messageTemplate || '');
    this.from = String(message?.from ?? '').trim();
    this.uuid = message?.uuid ?? null;

    this.schedules = schedulesOf(message)
      .map((schedule, i) => {
        const at = scheduleMoment(schedule);
        const zone = schedule.scheduleTimeZone || null;
        return {
          at,
          row: {
            key: schedule.id ?? `s-${i}`,
            when: formatWhen(at, now),
            exact: [schedule.scheduleDate?.slice(0, 10), schedule.scheduleTime?.slice(0, 5), zone]
              .filter(Boolean)
              .join(' '),
            zone,
            past: !!at && at.getTime() <= now.getTime(),
            countdown: formatCountdown(at, now),
            status: String(schedule.status ?? ''),
            stopped: isStopped(schedule.status),
          } as ScheduleRow,
        };
      })
      .sort((a, b) => (a.at?.getTime() ?? Infinity) - (b.at?.getTime() ?? Infinity))
      .map(({ row }) => row);

    const delivery = messageDelivery(message, now);
    this.deliveryStatus = delivery.status;
    this.sentAt = delivery.sentAt;
  }

  deleteMessage(id: number) {
    const data = {
      ...this.campaignData,
      engagementMessages: [
        ...this.campaignData.engagementMessages.filter(
          (item) => item.id !== this.message.id
        ),
      ],
    };
    this.campaignsService
      .editCampaign(this.campaignData.id, data, false)
      .subscribe(() => {
        this.showDelete = false;
      });
  }
}
